import * as React from 'react';
import { create } from 'zustand';

/**
 * Intake watch store (T-0157) — how the requirement library's intake screens
 * talk to the app-wide run watcher (`IntakeRunWatcher`, mounted in the app
 * shell).
 *
 *   - `nudge(runId)`: a run was just started (an upload, "Read again",
 *     "Start again"). The watcher looks at once instead of waiting for a
 *     page load or a focus, and counts the run as one it has seen running.
 *   - `onScreen`: runs an open intake sheet is showing. The sheet already
 *     says when such a run fails or is ready, so the watcher raises no toast
 *     for it (it still counts the change as announced).
 */

export interface IntakeWatchState {
  /** Grows by one per nudge; the watcher polls when it changes. */
  nudges: number;
  /** Runs nudged since the watcher last looked. */
  nudgedRunIds: ReadonlyArray<string>;
  /** Run id -> number of open sheets showing it. */
  onScreen: Readonly<Record<string, number>>;
  nudge: (runId?: string) => void;
  takeNudged: () => string[];
  show: (runId: string) => void;
  hide: (runId: string) => void;
}

export const useIntakeWatch = create<IntakeWatchState>((set, get) => ({
  nudges: 0,
  nudgedRunIds: [],
  onScreen: {},
  nudge: (runId) =>
    set((s) => ({
      nudges: s.nudges + 1,
      nudgedRunIds: runId ? [...s.nudgedRunIds, runId] : s.nudgedRunIds,
    })),
  takeNudged: () => {
    const ids = [...get().nudgedRunIds];
    if (ids.length > 0) set({ nudgedRunIds: [] });
    return ids;
  },
  show: (runId) =>
    set((s) => ({ onScreen: { ...s.onScreen, [runId]: (s.onScreen[runId] ?? 0) + 1 } })),
  hide: (runId) =>
    set((s) => {
      const left = (s.onScreen[runId] ?? 0) - 1;
      const next = { ...s.onScreen };
      if (left > 0) next[runId] = left;
      else delete next[runId];
      return { onScreen: next };
    }),
}));

/** Tell the run watcher a run was just started. Callable outside React. */
export function nudgeIntakeWatch(runId?: string): void {
  useIntakeWatch.getState().nudge(runId);
}

/** True while an open intake sheet shows this run. */
export function isIntakeRunOnScreen(runId: string): boolean {
  return (useIntakeWatch.getState().onScreen[runId] ?? 0) > 0;
}

/** Mark a run as shown by this component while it is mounted with it. */
export function useIntakeRunOnScreen(runId: string | null): void {
  React.useEffect(() => {
    if (!runId) return;
    const { show, hide } = useIntakeWatch.getState();
    show(runId);
    return () => hide(runId);
  }, [runId]);
}
